/**
 * iOS install banner / "Add to Home Screen" detection helpers.
 *
 * Why this exists:
 *   Allure ships as a Trusted Web Activity on Google Play (Android) but
 *   has no App Store presence yet. iOS users can use the full web app in
 *   Safari, BUT they only get Web Push notifications if they add the PWA
 *   to their Home Screen (Apple limitation — push doesn't work in
 *   Safari tabs).
 *
 *   So we surface a friendly, dismissible banner that nudges *real
 *   Mobile Safari* iOS users to "Add to Home Screen". Once dismissed
 *   the banner stays hidden for 24h and then reappears (until the
 *   user actually installs the PWA — at that point `navigator
 *   .standalone` flips to true and we permanently suppress it).
 *
 *   The banner does NOT show inside Chrome-iOS / Firefox-iOS or any
 *   in-app WebView (Gmail, Outlook, Instagram, …) because
 *   Add-to-Home-Screen is Safari-only on iOS. Those users get the
 *   full-screen "Open in Safari" nudge (see InAppBrowserBanner)
 *   instead.
 */

import { isInAppBrowser } from './inAppBrowser';

// New timestamp-based key: stores the ms timestamp of the last dismiss.
const IOS_BANNER_DISMISSED_AT_KEY = 'allure-ios-install-banner-dismissed-at';

// Legacy boolean keys we still honour for one migration cycle so
// existing users don't see the banner immediately on next load.
const LEGACY_KEYS = [
  'allure-ios-install-banner-dismissed-v1',
  'allure_ios_install_dismissed',
];

// Re-show interval after a dismiss. 24h feels persistent-but-not-spammy.
const RESHOW_INTERVAL_MS = 24 * 60 * 60 * 1000;

export function isIOSDevice() {
  try {
    if (typeof window === 'undefined' || !window.navigator) return false;
    const ua = window.navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    // iPadOS 13+ identifies as Mac but supports touch.
    if (
      /Macintosh/.test(ua) &&
      typeof window.navigator.maxTouchPoints === 'number' &&
      window.navigator.maxTouchPoints > 1
    ) {
      return true;
    }
    return false;
  } catch (_err) {
    return false;
  }
}

export function isStandalonePWA() {
  try {
    if (typeof window === 'undefined') return false;
    if (
      window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches
    ) {
      return true;
    }
    if (window.navigator && window.navigator.standalone === true) {
      return true;
    }
    return false;
  } catch (_err) {
    return false;
  }
}

/**
 * True when the banner should stay hidden — i.e. the user dismissed
 * it less than 24h ago. Migrates any legacy "dismissed=1" flag on
 * first read so returning users get one final 24h grace window.
 */
export function isIOSInstallBannerDismissed() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const raw = window.localStorage.getItem(IOS_BANNER_DISMISSED_AT_KEY);
    if (raw) {
      const ts = Number(raw);
      if (Number.isFinite(ts) && Date.now() - ts < RESHOW_INTERVAL_MS) {
        return true;
      }
      return false;
    }
    // Migrate: if a legacy "dismissed" flag is set, stamp it now so
    // the user gets a 24h grace window instead of an instant re-nudge.
    for (const key of LEGACY_KEYS) {
      if (window.localStorage.getItem(key)) {
        window.localStorage.setItem(
          IOS_BANNER_DISMISSED_AT_KEY,
          String(Date.now())
        );
        try { window.localStorage.removeItem(key); } catch (_e) { /* noop */ }
        return true;
      }
    }
    return false;
  } catch (_err) {
    return false;
  }
}

export function dismissIOSInstallBanner() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(
      IOS_BANNER_DISMISSED_AT_KEY,
      String(Date.now())
    );
  } catch (_err) {
    /* ignore */
  }
}

/**
 * True when ALL conditions hold:
 *   - device is iOS (any browser — Safari, Chrome-iOS, Firefox-iOS,
 *     Edge-iOS, DuckDuckGo, Brave; the copy tells them how to install
 *     regardless of which iOS browser they're currently in).
 *   - app is NOT already installed (not in standalone PWA mode).
 *   - we're NOT inside a mail / social in-app WebView (Gmail,
 *     Outlook, Instagram, …) — those get the full-screen "Open in
 *     Safari" escape sheet instead, so we don't stack two prompts.
 *   - user has not dismissed the banner in the last 24h.
 */
export function shouldShowIOSInstallBanner() {
  if (!isIOSDevice()) return false;
  if (isStandalonePWA()) return false;
  if (isInAppBrowser()) return false;
  if (isIOSInstallBannerDismissed()) return false;
  return true;
}
